import { Component, OnInit, EventEmitter, Output, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil, tap } from 'rxjs/operators';
import { fundsOriginLocation, fundsOriginOption } from './funds-origin';
import { RucExactLengthValidator } from 'src/app/shared/validators/ruc-exact-length.validator';
import { RequiredValidator } from 'src/app/shared/validators/required.validator';
import { LettersOnlyValidator } from 'src/app/shared/validators/letters-only.validators';
import { IDLengthBeneficiaryValidator } from 'src/app/shared/validators/id-length-beneficiary.validator';
import { IDFormatBeneficiaryValidator } from 'src/app/shared/validators/id-format-beneficiary.validator';

@Component({
  selector: 'app-laft-beneficiary-form',
  templateUrl: './laft-beneficiary-form.component.html',
  styleUrls: ['./laft-beneficiary-form.component.scss'],
})
export class LaftBeneficiaryFormComponent implements OnInit {
  @Output() nextStep = new EventEmitter();
  @Output() formChangesEvent = new EventEmitter();
  @Input() initData: any;

  formSubmitSubject$ = new Subject();
  private destroy$ = new Subject<void>();
  public beneficiaryDataForm: FormGroup;
  public fundsOriginOptions = fundsOriginOption;
  public fundsOriginLocationOptions = fundsOriginLocation;
  public idTypeOptions = [
    { value: 'national id', text: 'DNI' },
    { value: 'foreigner card', text: 'Carnet de Extranjería' },
    { value: 'passport', text: 'Pasaporte' },
  ];

  constructor(private formBuilder: FormBuilder) {}

  ngOnInit(): void {
    this.beneficiaryDataForm = this.formBuilder.group({
      isBeneficiary: [true, [Validators.required]],
      beneficiaryRepresentativeType: [null, []],
      beneficiaryBusinessName: ['', []],
      beneficiaryRuc: ['', []],
      beneficiaryPowerType: ['acta', []],
      beneficiaryDocument: [null, []],
      beneficiaryPersonName: ['', []],
      beneficiaryPersonDocumentNumber: ['', []],
      beneficiaryPersonDocumentType: ['national id', []],
      beneficiaryPersonPowerType: ['escritura_publica', []],
      beneficiaryPersonDocument: [null, []],
      fundsOrigin: ['', [Validators.required]],
      fundsOriginOtherOption: ['', []],
      fundsOriginLocation: ['', [Validators.required]],
      fundsOriginLocationOtherOption: ['', []],
      isBeneficiaryPersonPep: [false, []],
      beneficiaryPersonCharge: ['', []],
      beneficiaryPersonInstitution: ['', []],
    });

    if (this.initData?.beneficiaryRepresentativeType) {
      this.beneficiaryDataForm
        .get('beneficiaryRepresentativeType')
        .setValue(this.initData?.beneficiaryRepresentativeType);
    } else {
      this.beneficiaryDataForm.get('beneficiaryRepresentativeType').setValue('persona_natural');
    }

    this.beneficiaryDataForm.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
      let cleanedData = this.removeEmptyFields(this.beneficiaryDataForm.value);
      cleanedData = this.removeBeneficiaryRepresentativeFields(cleanedData);
      this.formChangesEvent.emit(cleanedData);
    });

    this.formSubmitSubject$
      .pipe(
        tap(() => {
          this.beneficiaryDataForm.markAsDirty();
          this.beneficiaryDataForm.markAllAsTouched();
          Object.keys(this.beneficiaryDataForm.controls).forEach(key => {
            const control = this.beneficiaryDataForm.get(key);
            control?.updateValueAndValidity({ emitEvent: true });
          });
        }),
        filter(() => this.beneficiaryDataForm.valid)
      )
      .subscribe(() => {
        let cleanedData = this.removeEmptyFields(this.beneficiaryDataForm.value);
        cleanedData = this.removeBeneficiaryRepresentativeFields(cleanedData);
        this.nextStep.next(cleanedData);
      });

    this.updateFieldValidation();
    this.populateFormData();
  }

  onContinue(): void {
    this.formSubmitSubject$.next();
  }

  private removeEmptyFields(data: any): any {
    const cleaned = { ...data };

    if (!cleaned.fundsOriginOtherOption || cleaned.fundsOriginOtherOption.trim() === '') {
      cleaned.fundsOriginOtherOption = '';
    }

    if (!cleaned.fundsOriginLocationOtherOption || cleaned.fundsOriginLocationOtherOption.trim() === '') {
      cleaned.fundsOriginLocationOtherOption = '';
    }

    return cleaned;
  }

  private removeBeneficiaryRepresentativeFields(data: any): any {
    const cleaned = { ...data };

    if (!cleaned.isBeneficiary && cleaned.beneficiaryRepresentativeType === 'persona_juridica') {
      cleaned.beneficiaryPersonName = '';
      cleaned.beneficiaryPersonDocumentNumber = '';
      cleaned.beneficiaryPersonDocumentType = '';
      cleaned.beneficiaryPersonPowerType = '';
      cleaned.beneficiaryPersonDocument = null;
      cleaned.isBeneficiaryPersonPep = false;
      cleaned.beneficiaryPersonCharge = '';
      cleaned.beneficiaryPersonInstitution = '';
      return cleaned;
    }

    if (!cleaned.isBeneficiary && cleaned.beneficiaryRepresentativeType === 'persona_natural') {
      cleaned.beneficiaryBusinessName = '';
      cleaned.beneficiaryRuc = '';
      cleaned.beneficiaryPowerType = '';
      cleaned.beneficiaryDocument = null;

      if (!cleaned.isBeneficiaryPersonPep) {
        cleaned.beneficiaryPersonCharge = '';
        cleaned.beneficiaryPersonInstitution = '';
      }

      return cleaned;
    }

    cleaned.beneficiaryRepresentativeType = '';
    cleaned.beneficiaryBusinessName = '';
    cleaned.beneficiaryRuc = '';
    cleaned.beneficiaryPowerType = '';
    cleaned.beneficiaryDocument = null;
    cleaned.beneficiaryPersonName = '';
    cleaned.beneficiaryPersonDocumentNumber = '';
    cleaned.beneficiaryPersonDocumentType = '';
    cleaned.beneficiaryPersonPowerType = '';
    cleaned.beneficiaryPersonDocument = null;
    cleaned.isBeneficiaryPersonPep = false;
    cleaned.beneficiaryPersonCharge = '';
    cleaned.beneficiaryPersonInstitution = '';
    return cleaned;
  }

  updateFieldValidation() {
    this.beneficiaryDataForm
      .get('isBeneficiary')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (!value) {
          this.beneficiaryDataForm.get('beneficiaryRepresentativeType').setValidators([Validators.required]);
        } else {
          this.beneficiaryDataForm.get('beneficiaryRepresentativeType').clearValidators();
          this.beneficiaryDataForm.get('beneficiaryBusinessName').clearValidators();
          this.beneficiaryDataForm.get('beneficiaryRuc').clearValidators();
          this.beneficiaryDataForm.get('beneficiaryPowerType').clearValidators();
          this.beneficiaryDataForm.get('beneficiaryDocument').clearValidators();
          this.beneficiaryDataForm.get('beneficiaryPersonName').clearValidators();
          this.beneficiaryDataForm.get('beneficiaryPersonDocumentNumber').clearValidators();
          this.beneficiaryDataForm.get('beneficiaryPersonDocumentType').clearValidators();
          this.beneficiaryDataForm.get('beneficiaryPersonPowerType').clearValidators();
          this.beneficiaryDataForm.get('beneficiaryPersonDocument').clearValidators();
          this.beneficiaryDataForm.get('isBeneficiaryPersonPep').clearValidators();
          this.beneficiaryDataForm.get('beneficiaryPersonCharge').clearValidators();
          this.beneficiaryDataForm.get('beneficiaryPersonInstitution').clearValidators();

          this.beneficiaryDataForm.patchValue({
            beneficiaryBusinessName: '',
            beneficiaryRuc: '',
            beneficiaryPowerType: 'acta',
            beneficiaryDocument: null,
            beneficiaryPersonName: '',
            beneficiaryPersonDocumentNumber: '',
            beneficiaryPersonDocumentType: 'national id',
            beneficiaryPersonPowerType: 'escritura_publica',
            beneficiaryPersonDocument: null,
            isBeneficiaryPersonPep: false,
            beneficiaryPersonCharge: '',
            beneficiaryPersonInstitution: '',
          });

          this.beneficiaryDataForm.get('beneficiaryBusinessName').updateValueAndValidity();
          this.beneficiaryDataForm.get('beneficiaryRuc').updateValueAndValidity();
          this.beneficiaryDataForm.get('beneficiaryPowerType').updateValueAndValidity();
          this.beneficiaryDataForm.get('beneficiaryDocument').updateValueAndValidity();
          this.beneficiaryDataForm.get('beneficiaryPersonName').updateValueAndValidity();
          this.beneficiaryDataForm.get('beneficiaryPersonDocumentNumber').updateValueAndValidity();
          this.beneficiaryDataForm.get('beneficiaryPersonDocumentType').updateValueAndValidity();
          this.beneficiaryDataForm.get('beneficiaryPersonPowerType').updateValueAndValidity();
          this.beneficiaryDataForm.get('beneficiaryPersonDocument').updateValueAndValidity();
          this.beneficiaryDataForm.get('isBeneficiaryPersonPep').updateValueAndValidity();
          this.beneficiaryDataForm.get('beneficiaryPersonCharge').updateValueAndValidity();
          this.beneficiaryDataForm.get('beneficiaryPersonInstitution').updateValueAndValidity();
        }

        this.beneficiaryDataForm.get('beneficiaryRepresentativeType').updateValueAndValidity();
        this.updatePepFieldsValidation();
      });

    this.beneficiaryDataForm
      .get('beneficiaryRepresentativeType')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        const businessNameControl = this.beneficiaryDataForm.get('beneficiaryBusinessName');
        const rucControl = this.beneficiaryDataForm.get('beneficiaryRuc');
        const powerTypeControl = this.beneficiaryDataForm.get('beneficiaryPowerType');
        const documentControl = this.beneficiaryDataForm.get('beneficiaryDocument');

        const personNameControl = this.beneficiaryDataForm.get('beneficiaryPersonName');
        const personDocumentTypeControl = this.beneficiaryDataForm.get('beneficiaryPersonDocumentType');
        const personDocumentNumberControl = this.beneficiaryDataForm.get('beneficiaryPersonDocumentNumber');
        const personPowerTypeControl = this.beneficiaryDataForm.get('beneficiaryPersonPowerType');
        const personDocumentControl = this.beneficiaryDataForm.get('beneficiaryPersonDocument');

        if (value === 'persona_juridica' && !this.beneficiaryDataForm.get('isBeneficiary').value) {
          businessNameControl.setValidators([Validators.required]);
          rucControl.setValidators([Validators.required, RucExactLengthValidator(11)]);
          powerTypeControl.setValidators([Validators.required]);
          documentControl.setValidators([Validators.required]);

          personNameControl.clearValidators();
          personDocumentNumberControl.clearValidators();
          personDocumentTypeControl.clearValidators();
          personPowerTypeControl.clearValidators();
          personDocumentControl.clearValidators();

          personNameControl.setValue('');
          personDocumentNumberControl.setValue('');
          personDocumentTypeControl.setValue('national id');
          personPowerTypeControl.setValue('escritura_publica');
          personDocumentControl.setValue(null);
        } else if (value === 'persona_natural' && !this.beneficiaryDataForm.get('isBeneficiary').value) {
          personNameControl.setValidators([RequiredValidator, LettersOnlyValidator]);
          personDocumentTypeControl.setValidators([Validators.required]);
          personDocumentNumberControl.setValidators([
            RequiredValidator,
            IDFormatBeneficiaryValidator,
            IDLengthBeneficiaryValidator,
          ]);
          personPowerTypeControl.setValidators([Validators.required]);
          personDocumentControl.setValidators([Validators.required]);

          businessNameControl.clearValidators();
          rucControl.clearValidators();
          powerTypeControl.clearValidators();
          documentControl.clearValidators();

          businessNameControl.setValue('');
          rucControl.setValue('');
          powerTypeControl.setValue('acta');
          documentControl.setValue(null);
        } else {
          businessNameControl.clearValidators();
          rucControl.clearValidators();
          powerTypeControl.clearValidators();
          documentControl.clearValidators();

          personNameControl.clearValidators();
          personDocumentNumberControl.clearValidators();
          personDocumentTypeControl.clearValidators();
          personPowerTypeControl.clearValidators();
          personDocumentControl.clearValidators();
        }

        businessNameControl.updateValueAndValidity();
        rucControl.updateValueAndValidity();
        powerTypeControl.updateValueAndValidity();
        documentControl.updateValueAndValidity();

        personNameControl.updateValueAndValidity();
        personDocumentTypeControl.updateValueAndValidity();
        personDocumentNumberControl.updateValueAndValidity();
        personPowerTypeControl.updateValueAndValidity();
        personDocumentControl.updateValueAndValidity();

        this.updatePepFieldsValidation();
      });

    this.beneficiaryDataForm
      .get('fundsOrigin')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        const fundsOriginOtherControl = this.beneficiaryDataForm.get('fundsOriginOtherOption');

        if (value === 'other') {
          fundsOriginOtherControl.setValidators([Validators.required]);
        } else {
          fundsOriginOtherControl.clearValidators();
          fundsOriginOtherControl.setValue('');
        }

        fundsOriginOtherControl.updateValueAndValidity();
      });

    this.beneficiaryDataForm
      .get('fundsOriginLocation')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        const fundsOriginLocationOtherControl = this.beneficiaryDataForm.get('fundsOriginLocationOtherOption');

        if (value === 'other') {
          fundsOriginLocationOtherControl.setValidators([Validators.required]);
        } else {
          fundsOriginLocationOtherControl.clearValidators();
          fundsOriginLocationOtherControl.setValue('');
        }

        fundsOriginLocationOtherControl.updateValueAndValidity();
      });

    this.beneficiaryDataForm
      .get('isBeneficiaryPersonPep')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updatePepFieldsValidation();
      });
  }

  private updatePepFieldsValidation(): void {
    const isBeneficiary = this.beneficiaryDataForm.get('isBeneficiary').value;
    const representativeType = this.beneficiaryDataForm.get('beneficiaryRepresentativeType').value;
    const isPep = this.beneficiaryDataForm.get('isBeneficiaryPersonPep').value;
    const chargeControl = this.beneficiaryDataForm.get('beneficiaryPersonCharge');
    const institutionControl = this.beneficiaryDataForm.get('beneficiaryPersonInstitution');

    if (!isBeneficiary && representativeType === 'persona_natural' && isPep) {
      chargeControl.setValidators([Validators.required]);
      institutionControl.setValidators([Validators.required]);
    } else {
      chargeControl.clearValidators();
      institutionControl.clearValidators();
      if (!isPep) {
        chargeControl.setValue('');
        institutionControl.setValue('');
      }
    }
    chargeControl.updateValueAndValidity();
    institutionControl.updateValueAndValidity();
  }

  get isPersonalNatural() {
    return this.beneficiaryDataForm.get('beneficiaryRepresentativeType').value === 'persona_natural';
  }

  get isBeneficiaryPersonPep() {
    return this.beneficiaryDataForm.get('isBeneficiaryPersonPep').value;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private populateFormData(): void {
    this.beneficiaryDataForm.patchValue({
      isBeneficiary: this.initData?.isBeneficiary ?? true,
      fundsOrigin: this.initData?.fundsOrigin || '',
      fundsOriginOtherOption: this.initData?.fundsOriginOtherOption || '',
      fundsOriginLocation: this.initData?.fundsOriginLocation || '',
      fundsOriginLocationOtherOption: this.initData?.fundsOriginLocationOtherOption || '',
      isBeneficiaryPersonPep: this.initData?.isBeneficiaryPersonPep || false,
      beneficiaryRepresentativeType: this.initData?.beneficiaryRepresentativeType || 'persona_natural',
      beneficiaryBusinessName: this.initData?.beneficiaryBusinessName || '',
      beneficiaryRuc: this.initData?.beneficiaryRuc || '',
      beneficiaryPowerType: this.initData?.beneficiaryPowerType || 'acta',
      beneficiaryPersonName: this.initData?.beneficiaryPersonName || '',
      beneficiaryPersonDocumentNumber: this.initData?.beneficiaryPersonDocumentNumber || '',
      beneficiaryPersonDocument: this.initData?.beneficiaryPersonDocument || '',
      beneficiaryDocument: this.initData?.beneficiaryDocument || '',
      beneficiaryPersonDocumentType:
        this.initData?.beneficiaryPersonDocumentType ??
        this.beneficiaryDataForm.get('beneficiaryPersonDocumentType')?.value ??
        'national id',
      beneficiaryPersonPowerType: this.initData?.beneficiaryPersonPowerType || 'escritura_publica',
      beneficiaryPersonCharge: this.initData?.beneficiaryPersonCharge || '',
      beneficiaryPersonInstitution: this.initData?.beneficiaryPersonInstitution || '',
    });
  }
}
